/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode, useEffect, useState } from "react";
import { ACCESS_TOKEN, FCM_TOKEN } from "../constants";
import { useNavigate } from "react-router-dom";
import { isIOS } from "react-device-detect";
import { UserModel } from "../apis/types";
import { FrontDeskApi } from "../apis/frontDesk.api";
import { deleteFCMToken } from "../firebase";
import { CoachAvailabilityWeekRanges } from "../types/types";
import { coachDefaultCoachAvailabilityWeekRanges } from "../utils/data";
import {
  getAfterNextWeekRange,
  getEndOfWeek,
  getNextWeekRange,
} from "../utils/utils";
import { datadogRum } from "@datadog/browser-rum";
import { MOBILE_SCREEN_WIDTH, TAB_SCREEN_WIDTH } from "../utils/constants";

type FrontDeskContextType = {
  isAuthenticated?: boolean | null;
  onLogOut: () => void;
  onMySchedule: () => void;
  fetchMe: (callback?: any) => void;
  setIosInstructions: React.Dispatch<React.SetStateAction<boolean>>;
  setNotificationPermission: React.Dispatch<
    React.SetStateAction<NotificationPermission | undefined>
  >;
  iosInstructions: boolean;
  notificationPermission: NotificationPermission | undefined;
  isShowIosInstructions: () => void;
  getFCMToken: () => string;
  staffModel: UserModel | undefined;
  setFCMToken: (value: string) => void;
  onBookPro: () => void;
  coachAvailabilityWeekRanges: CoachAvailabilityWeekRanges;
  onCoachAvailability: () => void;
  onOrders: () => void;
  onReedemLoyaltyPoints: () => void;
  initialRoute: string | null;
  setInitialRoute: React.Dispatch<React.SetStateAction<string | null>>;
  onMatchList: () => void;
  isMobile: boolean;
  isTab: boolean;
  setIsMobile: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTab: React.Dispatch<React.SetStateAction<boolean>>;
};

const FontDeskContext = React.createContext<FrontDeskContextType>({
  onLogOut: () => {},
  fetchMe: () => {},
  setIosInstructions: () => {},
  setNotificationPermission: () => {},
  iosInstructions: false,
  notificationPermission: undefined,
  isShowIosInstructions: () => {},
  getFCMToken: () => "",
  staffModel: undefined,
  setFCMToken: (value: string) => {},
  onBookPro: () => {},
  coachAvailabilityWeekRanges: coachDefaultCoachAvailabilityWeekRanges,
  onCoachAvailability: () => {},
  onOrders: () => {},
  onMySchedule: () => {},
  onReedemLoyaltyPoints: () => {},
  initialRoute: null,
  setInitialRoute: () => {},
  onMatchList: () => {},
  isMobile: false,
  isTab: false,
  setIsMobile: () => {},
  setIsTab: () => {},
});

type FontDeskProviderProps = {
  children: ReactNode | ReactNode[];
};

function FontDeskProvider(props: FontDeskProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [iosInstructions, setIosInstructions] = useState(false);
  const [initialRoute, setInitialRoute] = useState<string | null>(null);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>();
  const [staffModel, setStaffModel] = useState<UserModel>();
  const [coachAvailabilityWeekRanges, setCoachAvailabilityWeekRanges] =
    useState<CoachAvailabilityWeekRanges>(
      coachDefaultCoachAvailabilityWeekRanges
    );
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_SCREEN_WIDTH
  );

  const [isTab, setIsTab] = useState(
    window.innerWidth < TAB_SCREEN_WIDTH && !isMobile
  );

  const isShowIosInstructions = () => {
    if (!("Notification" in window)) {
      if (isIOS) {
        setIosInstructions(true);
      }
    } else {
      setNotificationPermission(Notification.permission);
    }
  };

  const navigate = useNavigate();

  const dataDogInit = () => {
    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID ?? "",
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN ?? "",
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: "datadoghq.com",
      service: "staff-web",
      env: process.env.REACT_APP_DATADOG_ENV,
      // Specify a version number to identify the deployed version of your application in Datadog
      // version: '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });
  };

  useEffect(() => {
    if (
      process.env.REACT_APP_DATADOG_APPLICATION_ID &&
      process.env.REACT_APP_DATADOG_CLIENT_TOKEN &&
      process.env.REACT_APP_DATADOG_ENV
    ) {
      dataDogInit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    process.env.REACT_APP_DATADOG_APPLICATION_ID,
    process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    process.env.REACT_APP_DATADOG_ENV,
  ]);

  useEffect(() => {
    fetchMe();
    const today = new Date();
    const currentWeekStart = today;
    const currentWeekEnd = getEndOfWeek(today);
    const { start: nextWeekStart, end: nextWeekEnd } = getNextWeekRange(today);
    const { start: afterNextWeekStart, end: afterNextWeekEnd } =
      getAfterNextWeekRange(today);
    setCoachAvailabilityWeekRanges({
      currentWeekStart,
      currentWeekEnd,
      nextWeekStart,
      nextWeekEnd,
      afterNextWeekStart,
      afterNextWeekEnd,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogOut = () => {
    navigate("/otp-login");
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(FCM_TOKEN);
    setStaffModel(undefined);
    setIsAuthenticated(false);
    deleteFCMToken();
  };

  const onBookPro = () => {
    navigate("/book-pro");
  };
  const onCoachAvailability = () => {
    navigate("/coach-availability");
  };
  const onOrders = () => {
    navigate("/orders");
  };

  const onReedemLoyaltyPoints = () => {
    navigate("/reedem-loyalty-points");
  };

  const onMySchedule = () => {
    navigate("/coach-plans");
  };

  const onMatchList = () => {
    navigate("/match-list");
  };
  const fetchMe = async (callback?: () => null) => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    isShowIosInstructions();

    if (token) {
      const user = FrontDeskApi.getMe();
      user.then((response) => {
        setStaffModel(response);
        datadogRum.setUser({ id: response.userId });
      });
    }

    setIsAuthenticated(!!token);
  };

  const getFCMToken = () => {
    return localStorage.getItem(FCM_TOKEN) ?? "";
  };

  const setFCMToken = (token: string) => {
    localStorage.setItem(FCM_TOKEN, token);
  };

  return (
    <FontDeskContext.Provider
      value={{
        onLogOut,
        isAuthenticated,
        fetchMe,
        setIosInstructions,
        setNotificationPermission,
        iosInstructions,
        notificationPermission,
        isShowIosInstructions,
        getFCMToken,
        staffModel,
        setFCMToken,
        onBookPro,
        coachAvailabilityWeekRanges,
        onCoachAvailability,
        onOrders,
        onMySchedule,
        onReedemLoyaltyPoints,
        initialRoute,
        setInitialRoute,
        onMatchList,
        isMobile,
        isTab,
        setIsMobile,
        setIsTab,
      }}
      {...props}
    />
  );
}

function useFontDesk() {
  const context = React.useContext(FontDeskContext);

  if (context === undefined) {
    throw new Error("useCoach must be used within a CoachProvider");
  }
  return context;
}

export { FontDeskProvider, useFontDesk };
